import { Crown as CrownIcon, ShieldCheck, HeartHandshake, Network, Sparkles } from "lucide-react";
import { Reveal, ChapterHeading } from "@/components/Reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBsYXd5ZXIlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzg5MTQxNDEwfDA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: CrownIcon, title: "Women-Founded", text: "Built and run by women who know the industry from the inside.", testId: "value-card-women-founded" },
  { icon: ShieldCheck, title: "Trusted", text: "Every partner is vetted, licensed, and held to our standard.", testId: "value-card-trusted" },
  { icon: HeartHandshake, title: "Compassionate", text: "You are a person in crisis, never a case number.", testId: "value-card-compassionate" },
  { icon: Network, title: "Connected", text: "Arizona headquarters, allied professionals across 24 states.", testId: "value-card-connected" },
  { icon: Sparkles, title: "Empowering", text: "We hand you back control when everything feels chaotic.", testId: "value-card-empowering" },
];

export const About = () => (
  <section id="about" data-testid="about-section" className="scroll-mt-24 py-24 sm:py-32 px-4 sm:px-8 relative">
    <div className="max-w-7xl mx-auto">
      <ChapterHeading
        eyebrow="01 // ORIGIN & VALUES"
        title="Founded by Women Who Refuse to Let Victims Be Taken Advantage Of"
        eyebrowTestId="about-chapter-num"
        titleTestId="about-headline"
      />
      <div className="mt-14 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p data-testid="about-manifesto-text" className="font-editorial text-lg sm:text-xl text-[#D4DFE6] leading-relaxed">
              For over a decade, we have stood between accident victims and the machine that profits from
              their confusion. From our Arizona headquarters, Collision Queens Connect orchestrates a
              hand-vetted alliance of attorneys, doctors, body shops, and rental providers across 24 states.
            </p>
            <p className="mt-6 text-sm sm:text-base text-[#8EA3B0] leading-relaxed">
              The hours after a crash are when people make the decisions that shape their recovery &mdash;
              physical, financial, and emotional. We exist so you never make those decisions alone, rushed,
              or pressured by an adjuster.
            </p>
            <blockquote className="mt-10 border-l-2 border-[#C9A24B] pl-6 py-2">
              <p className="font-editorial italic text-lg sm:text-xl text-[#FBF8F2] leading-relaxed">
                &ldquo;We take over the calls, the adjuster games, the repair vetting, and the medical liens
                &mdash; so you can focus on healing.&rdquo;
              </p>
            </blockquote>
            <div className="mt-10 relative hidden sm:block">
              <div className="absolute -inset-2 border border-[#C9A24B]/25 -translate-x-3 translate-y-3 pointer-events-none" />
              <img src={ABOUT_IMG} alt="A Collision Queens advocate reviewing a referral with care" className="relative w-full h-64 object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 grid sm:grid-cols-2 gap-5 content-start">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className={i === 4 ? "sm:col-span-2" : ""}>
              <div
                data-testid={v.testId}
                className="group h-full bg-[#092237] border border-[#C9A24B]/20 p-7 hover:bg-[#0C2E4A] hover:border-[#C9A24B]/50 hover:-translate-y-1 transition-all duration-500"
              >
                <v.icon className="w-7 h-7 text-[#C9A24B] transition-transform duration-500 group-hover:scale-110" />
                <h3 className="mt-4 font-display font-bold text-[#FBF8F2] text-base tracking-wider">{v.title}</h3>
                <p className="mt-2 text-sm text-[#8EA3B0] leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
